import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, } from '@angular/core';
import videojs from 'video.js';
import { VideoEvent } from "@core/models/videojs.model";
import { VideojsService } from "@core/services/videojs/videojs.service";
import 'videojs-contrib-quality-levels';
import 'videojs-hls-quality-selector';
import 'videojs-event-tracking';
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";

@Component({
  selector: 'app-videojs',
  templateUrl: './videojs.component.html',
  standalone: true,
  styleUrls: ['./videojs.component.scss']
})
export class VideojsComponent implements OnInit, OnDestroy {
  @ViewChild('target', { static: true }) target: ElementRef;

  @Input() videoUrl: string;
  @Input() mediaId: string;
  @Input() posterUrl: string;
  @Input() options: videojs.PlayerOptions;

  videoOptions: videojs.PlayerOptions;
  player: videojs.Player;

  private alive$ = new Subject<void>();

  constructor(
    private videoJsService: VideojsService,
  ) { }

  ngOnInit(): void {
    this.videoOptions = {
      controls: true,
      autoplay: false,
      plugins: {
        eventTracking: true
      },
      sources: [
        {
          src: this.videoUrl,
          type: 'video/mp4'
        }
      ],
      fluid: true
    };
    this.videoOptions = Object.assign(this.videoOptions, this.options);
    if (this.posterUrl) {
      this.videoOptions.poster = this.posterUrl;
    }

    this.player = videojs(this.target.nativeElement, this.videoOptions, function onPlayerReady() {
      this.hlsQualitySelector();
    });
    this.player.on('tracking:first-quarter', (e, data) => this.emitEvent('first-quarter', data));
    this.player.on('tracking:second-quarter', (e, data) => this.emitEvent('second-quarter', data));
    this.player.on('tracking:third-quarter', (e, data) => this.emitEvent('third-quarter', data));
    this.player.on('tracking:fourth-quarter', (e, data) => this.emitEvent('fourth-quarter', data));

    this.player.on('tracking:firstplay', (e, data) => this.emitEvent('firstplay', data));
  }

  ngOnDestroy(): void {
    if (this.player) {
      this.player.dispose();
    }
    this.alive$.next();
    this.alive$.complete();
  }

  private generateDeviceId(): string {
    const navigatorInfo = window.navigator;
    const screenInfo = window.screen;
    const uid = [
      navigatorInfo.userAgent,
      navigatorInfo.language,
      screenInfo.height,
      screenInfo.width,
      screenInfo.colorDepth,
      new Date().getTimezoneOffset(),
    ].join('');

    let hash = 0;
    for (let i = 0; i < uid.length; i++) {
      const char = uid.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return `${Math.abs(hash).toString(16)}`;
  }

  private getDeviceId(): string {
    const storedId = localStorage.getItem('userDeviceId');
    if (storedId) {
      return storedId;
    }

    const newId = this.generateDeviceId();
    localStorage.setItem('userDeviceId', newId);
    return newId;
  }

  private emitEvent(eventType: string, data: any): void {
    const event: VideoEvent = {
      event: eventType,
      date: new Date().toISOString(),
      pauseCount: data?.pauseCount,
      seekCount: data?.seekCount,
      currentTime: data?.currentTime,
      duration: data?.duration,
      deviceId: this.getDeviceId()
    };

    this.videoJsService.postMediaEvent(this.mediaId, event).pipe(
      takeUntil(this.alive$)
    ).subscribe();
  }
}
